import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import RibbonDisplay from '../../../../components/RibbonUtils';
import { formatRibbonDescription } from '../../../../utils/formatRibbonDescription';
import { fetchRosterFileRibbons } from '../rosterTools/api/rosterFileApi';
import { computeRibbonsColumnsPerRow, RIBBONS_MAX_COLUMNS, RIBBONS_MIN_COLUMNS } from './computeRibbonsColumnsPerRow';
import MemberRibbonAnchoredTooltip from './MemberRibbonAnchoredTooltip';
import { MEMBER_RIBBONS_GRID_VARIANTS } from './memberRibbonsGridClasses';
function joinHandlesLite(list) {
  const handles = (Array.isArray(list) ? list : [])
    .map((h) => (h || '').trim())
    .filter(Boolean);
  return handles.join(', ');
}

export default function MemberRibbonsGrid({
  variant = 'roster',
  handleId,
  scope = 'handle',
  handleMetaById,
  rosterDatasetRevision = 0,
  canSeeAllRibbons = false,
  panelTitle = null,
  columnsOffset = 0,
  gridRootSelector = '.member-dashboard__grid',
}) {
  const classes = MEMBER_RIBBONS_GRID_VARIANTS[variant] ?? MEMBER_RIBBONS_GRID_VARIANTS.roster;
  const autoFitColumns = variant === 'dashboard';

  const gridRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ribbons, setRibbons] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [autoColumns, setAutoColumns] = useState(4);
  const [openInfoRibbonId, setOpenInfoRibbonId] = useState(null);
  const [openHandleRibbonId, setOpenHandleRibbonId] = useState(null);
  const openInfoRef = useRef(null);
  const openInfoTooltipRef = useRef(null);
  const openHandleRef = useRef(null);
  const openHandleTooltipRef = useRef(null);

  const measureColumns = useCallback(() => {
    const el = gridRef.current;
    if (!el) return;

    const cardWidth = el.clientWidth;
    const gridRoot = gridRootSelector ? el.closest(gridRootSelector) : null;
    const referenceWidth = gridRoot?.clientWidth || cardWidth;
    let next = computeRibbonsColumnsPerRow(cardWidth, { referenceWidth });

    const offset = Number(columnsOffset);
    if (Number.isFinite(offset) && offset !== 0) {
      next = Math.min(
        RIBBONS_MAX_COLUMNS,
        Math.max(RIBBONS_MIN_COLUMNS, next + offset),
      );
    }

    setAutoColumns((current) => (current === next ? current : next));
  }, [columnsOffset, gridRootSelector]);

  const columnsPerRow = autoFitColumns ? autoColumns : 4;

  useEffect(() => {
    let mounted = true;

    async function run() {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchRosterFileRibbons(handleId, {
          scope,
          includeAll: showAll && !!canSeeAllRibbons,
        });
        if (!mounted) return;
        if (!data?.ok) {
          setError(data?.error || 'Error loading ribbons.');
          setRibbons([]);
          setLoading(false);
          return;
        }
        setRibbons(Array.isArray(data.ribbons) ? data.ribbons : []);
        setLoading(false);
      } catch (e) {
        if (!mounted) return;
        setError('Error loading ribbons.');
        setRibbons([]);
        setLoading(false);
      }
    }

    if (handleId) run();
    return () => {
      mounted = false;
    };
  }, [handleId, scope, rosterDatasetRevision, showAll, canSeeAllRibbons]);

  useEffect(() => {
    if (!canSeeAllRibbons && showAll) setShowAll(false);
  }, [canSeeAllRibbons, showAll]);

  useEffect(() => {
    setOpenInfoRibbonId(null);
    setOpenHandleRibbonId(null);
  }, [handleId, scope]);

  useEffect(() => {
    if (!autoFitColumns || loading || error) return undefined;

    const el = gridRef.current;
    if (!el) return undefined;

    measureColumns();

    const observer = new ResizeObserver(measureColumns);
    observer.observe(el);

    const gridRoot = gridRootSelector ? el.closest(gridRootSelector) : null;
    if (gridRoot) {
      observer.observe(gridRoot);
    }

    return () => observer.disconnect();
  }, [autoFitColumns, error, gridRootSelector, loading, measureColumns, ribbons.length, showAll]);

  const handleMap = useMemo(() => {
    return handleMetaById instanceof Map ? handleMetaById : new Map();
  }, [handleMetaById]);

  const orderedRibbons = useMemo(() => {
    const list = Array.isArray(ribbons) ? ribbons : [];
    if (!showAll) return list;
    return [...list].sort((a, b) => {
      const aReq = Number(a?.qtyRequirement) > 0 ? Number(a.qtyRequirement) : 1;
      const bReq = Number(b?.qtyRequirement) > 0 ? Number(b.qtyRequirement) : 1;
      const aCount = Number(a?.count) || 0;
      const bCount = Number(b?.count) || 0;
      const aEarned = aCount >= aReq;
      const bEarned = bCount >= bReq;
      if (aEarned !== bEarned) return aEarned ? -1 : 1;

      if (!aEarned && !bEarned) {
        const aMissing = Math.max(0, aReq - aCount);
        const bMissing = Math.max(0, bReq - bCount);
        if (aMissing !== bMissing) return aMissing - bMissing;
      }

      const an = (a?.ribbon?.ribbonName || '').toLowerCase();
      const bn = (b?.ribbon?.ribbonName || '').toLowerCase();
      return an.localeCompare(bn);
    });
  }, [ribbons, showAll]);

  const displayRibbons = useMemo(() => {
    if (!showAll) return orderedRibbons;
    return orderedRibbons.filter((item) => {
      const reqQty = Number(item?.qtyRequirement) > 0 ? Number(item.qtyRequirement) : 1;
      const cnt = Number(item?.count) || 0;
      const earned = cnt >= reqQty;
      if (reqQty === 1 && !earned) return false;
      return true;
    });
  }, [orderedRibbons, showAll]);

  useEffect(() => {
    if (!openInfoRibbonId) return undefined;
    const onDown = (e) => {
      const root = openInfoRef.current;
      const tip = openInfoTooltipRef.current;
      if (!root) return;
      if (root.contains(e.target) || tip?.contains(e.target)) return;
      setOpenInfoRibbonId(null);
    };
    window.addEventListener('mousedown', onDown, true);
    return () => window.removeEventListener('mousedown', onDown, true);
  }, [openInfoRibbonId]);

  useEffect(() => {
    if (!openHandleRibbonId) return undefined;
    const onDown = (e) => {
      const root = openHandleRef.current;
      const tip = openHandleTooltipRef.current;
      if (!root) return;
      if (root.contains(e.target) || tip?.contains(e.target)) return;
      setOpenHandleRibbonId(null);
    };
    window.addEventListener('mousedown', onDown, true);
    return () => window.removeEventListener('mousedown', onDown, true);
  }, [openHandleRibbonId]);

  const useDashboardChrome = variant === 'dashboard' && panelTitle;

  const toolbar = canSeeAllRibbons ? (
    <div className={classes.toolbar}>
      <div
        className="display-none"
        data-resource="private.navbar.roster.file.ribbons.all"
        data-action="read"
        data-route="/api/user/roster-file-ribbons"
        data-description="Personal hub — ribbons progression"
      />
      <button
        type="button"
        className={classes.toolbarBtn}
        onClick={() => setShowAll((v) => !v)}
      >
        {showAll ? 'hide ribbon progression' : 'Ribbon progression'}
      </button>
    </div>
  ) : null;

  if (loading) {
    if (useDashboardChrome) {
      return (
        <>
          <div className="member-dashboard__panel-card-head member-dashboard__ribbons-card-head">
            <h2 className="member-dashboard__panel-title">{panelTitle}</h2>
            {toolbar}
          </div>
          <div className="member-dashboard__panel-card-body">
            <div className={classes.loading}>Loading…</div>
          </div>
        </>
      );
    }
    return <div className={classes.loading}>Loading…</div>;
  }

  if (error) {
    if (useDashboardChrome) {
      return (
        <>
          <div className="member-dashboard__panel-card-head member-dashboard__ribbons-card-head">
            <h2 className="member-dashboard__panel-title">{panelTitle}</h2>
            {toolbar}
          </div>
          <div className="member-dashboard__panel-card-body">
            <div className={classes.error}>{error}</div>
          </div>
        </>
      );
    }
    return <div className={classes.error}>{error}</div>;
  }

  const gridContent = (
    <>
      <div ref={gridRef} className={classes.gridClass(columnsPerRow)}>
        {displayRibbons.map((item) => {
          const rid = item.ribbon?.id != null ? Number(item.ribbon.id) : null;
          const isOpen = rid != null && openInfoRibbonId != null && Number(openInfoRibbonId) === rid;
          const byHandleId =
            item?.byHandleId && typeof item.byHandleId === 'object' ? item.byHandleId : null;
          const reqQty = Number(item?.qtyRequirement) > 0 ? Number(item.qtyRequirement) : 1;
          const desc = formatRibbonDescription(item.ribbon?.description, reqQty);
          const cnt = Number(item?.count) || 0;
          const earned = cnt >= reqQty;
          const showProgressBar = showAll && reqQty > 1;
          const filledSeg = Math.min(cnt, reqQty);
          const childHandles =
            scope === 'cluster' && byHandleId
              ? Object.keys(byHandleId)
                  .map((k) => Number(k))
                  .filter((hid) => Number.isInteger(hid) && hid > 0)
                  .filter((hid) => handleMap.get(hid)?.isMaster === false)
                  .map((hid) => handleMap.get(hid)?.rosterHandle)
                  .filter(Boolean)
              : [];
          const hasChildContrib = childHandles.length > 0;
          const handleTip = hasChildContrib
            ? `done with ${joinHandlesLite(childHandles.map((h) => `@${h}`))}`
            : '';
          const isHandleOpen =
            rid != null && openHandleRibbonId != null && Number(openHandleRibbonId) === rid;

          return (
            <div
              key={item.ribbon?.id}
              className={[
                classes.ribbonCard,
                showAll && !earned ? classes.ribbonCardLocked : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {showProgressBar ? (
                <div
                  className={classes.progress}
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={reqQty}
                  aria-valuenow={filledSeg}
                  aria-label={`Ribbon progress ${filledSeg} of ${reqQty}`}
                >
                  <div className={classes.progressBarWrap}>
                    <div
                      className={[
                        classes.progressTrack,
                        earned ? classes.progressTrackComplete : '',
                        filledSeg === 0 ? classes.progressTrackEmpty : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {Array.from({ length: reqQty }, (_, i) => (
                        <div
                          key={i}
                          className={[
                            classes.progressSegment,
                            i < filledSeg ? classes.progressSegmentFilled : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          aria-hidden
                        />
                      ))}
                    </div>
                    <span className={classes.progressLabel}>
                      {filledSeg}/{reqQty}
                    </span>
                  </div>
                </div>
              ) : null}
              {hasChildContrib ? (
                <div
                  className={classes.handle}
                  ref={isHandleOpen ? openHandleRef : null}
                >
                  <button
                    type="button"
                    className={classes.handleBtn}
                    aria-label="Show child handle"
                    aria-expanded={isHandleOpen}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (rid == null) return;
                      setOpenHandleRibbonId((cur) =>
                        cur != null && Number(cur) === rid ? null : rid
                      );
                    }}
                  >
                    h
                  </button>
                  <MemberRibbonAnchoredTooltip
                    open={isHandleOpen}
                    anchorRef={openHandleRef}
                    tooltipRef={openHandleTooltipRef}
                    align="left"
                    className={classes.handleTooltip}
                    role="dialog"
                    aria-label="Ribbon handles"
                  >
                    {handleTip}
                  </MemberRibbonAnchoredTooltip>
                </div>
              ) : null}
              <RibbonDisplay ribbon={item.ribbon} />
              <div className={classes.name}>{item.ribbon?.ribbonName || '—'}</div>
              <div className={classes.info} ref={isOpen ? openInfoRef : null}>
                <button
                  type="button"
                  className={classes.infoBtn}
                  aria-label="Ribbon information"
                  aria-expanded={isOpen}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (rid == null) return;
                    setOpenInfoRibbonId((cur) =>
                      cur != null && Number(cur) === rid ? null : rid
                    );
                  }}
                >
                  i
                </button>
                <MemberRibbonAnchoredTooltip
                  open={isOpen}
                  anchorRef={openInfoRef}
                  tooltipRef={openInfoTooltipRef}
                  align="right"
                  className={classes.infoTooltip}
                  role="dialog"
                  aria-label="Ribbon description"
                >
                  {desc || '—'}
                </MemberRibbonAnchoredTooltip>
              </div>
            </div>
          );
        })}
      </div>

      {!displayRibbons.length ? (
        <div className={classes.placeholder}>No ribbons</div>
      ) : null}
    </>
  );

  if (useDashboardChrome) {
    return (
      <>
        <div className="member-dashboard__panel-card-head member-dashboard__ribbons-card-head">
          <h2 className="member-dashboard__panel-title">{panelTitle}</h2>
          {toolbar}
        </div>
        <div className="member-dashboard__panel-card-body">{gridContent}</div>
      </>
    );
  }

  return (
    <div className={classes.scroll}>
      {toolbar}
      {gridContent}
    </div>
  );
}
